import { Dispatch, SetStateAction, useMemo } from 'react';
import cn from 'classnames';
import { PATHS } from '../../../router/paths.ts';
import { MEDIA_QUERIES, useMediaQuery } from '@utils/hooks/use-media-query.tsx';
import Logo from '@components/shared/logo.tsx';
import { useLogout } from '@utils/api/users/use-logout.ts';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import { css, StyleSheet } from 'aphrodite';
import { useLocation, useNavigate } from 'react-router-dom';
import { useRoles } from '@utils/hooks/use-roles.ts';
import { Tooltip } from '@components/atoms/ui-kit/tooltip.tsx';
import {
  ActivitySquare,
  BookOpenCheck,
  Landmark,
  LogOut,
  Menu,
  MessageSquareMore,
  Settings,
  UserRound
} from 'lucide-react';
import { IS_ON_PREM } from '@utils/constants/app-constants.ts';

interface Props {
  setSidebarOpen: Dispatch<SetStateAction<boolean>>;
  layoutType?: 'editor' | 'main';
}

export default function TopNav(props: Props) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const location = useLocation();
  const { isSysOp } = useRoles();
  const [largeTabletAndAbove] = useMediaQuery(
    `(min-width: ${MEDIA_QUERIES.LARGE_TABLET})`
  );
  const { getColour, theme } = useThemeContext();

  const isEditorLayout = props.layoutType === 'editor';

  const { mutate: logout } = useLogout();

  const bg = getColour('secondLayer');
  const border = getColour('border');
  const text = getColour('normalText');
  const icon = getColour('icon');
  const active = getColour('activeText');

  const styles = useMemo(
    () =>
      StyleSheet.create({
        topNavContainer: {
          borderBottom: `1px solid ${border}`,
          boxShadow:
            '0 6px 16px -2px rgba(0,0,0,0.1), 0 3px 6px -2px rgba(0,0,0,0.06)',
          position: 'relative' as const,
          zIndex: 20
        },
        topNavContainerColor: {
          backgroundColor: bg,
          color: text
        },
        iconColor: {
          color: icon
        }
      }),
    [getColour]
  );

  const styleVars: React.CSSProperties = {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ['--tn-bg' as any]: bg,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ['--tn-border' as any]: border,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ['--tn-text' as any]: text,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ['--tn-icon' as any]: icon,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ['--tn-active' as any]: active,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ['--tn-hover-bg' as any]:
      theme === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)'
  };

  const shouldHideSidebarButton =
    location.pathname.startsWith('/caption-editor/') ||
    location.pathname.startsWith('/transcript-editor/');

  // route actives
  const STATUS_PATH = PATHS().STATUS;
  const PROFILE_PATH = PATHS().PROFILE;
  const isStatus = location.pathname === STATUS_PATH;
  const isProfile = location.pathname === PROFILE_PATH;

  const IconButton = ({
    label,
    onClick,
    children,
    isActive = false,
    as = 'button',
    href,
    target
  }: {
    label: string;
    onClick?: () => void;
    children: React.ReactNode;
    isActive?: boolean;
    as?: 'button' | 'a';
    href?: string;
    target?: string;
  }) => {
    const className = cn(
      'inline-flex h-9 w-9 items-center justify-center rounded-2xl transition-colors',
      'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
      theme === 'dark'
        ? 'focus-visible:ring-offset-gray-900'
        : 'focus-visible:ring-offset-white',
      isActive
        ? 'text-white bg-[var(--tn-active)]'
        : 'text-[var(--tn-icon)] hover:bg-[var(--tn-hover-bg)]'
    );

    if (as === 'a' && href) {
      return (
        <a
          href={href}
          target={target}
          rel={target === '_blank' ? 'noreferrer noopener' : undefined}
          className={className}
          aria-label={label}
          title={label}
        >
          {children}
        </a>
      );
    }

    return (
      <button
        type="button"
        className={className}
        onClick={onClick}
        aria-label={label}
        title={label}
      >
        {children}
      </button>
    );
  };

  return (
    <nav className="w-full h-16" style={styleVars}>
      <div
        className={cn(
          'flex h-16 items-center flex-row gap-x-4',
          'px-3 sm:px-4 lg:px-8',
          'shadow-sm lg:shadow-none',
          css(styles.topNavContainerColor, styles.topNavContainer)
        )}
      >
        {largeTabletAndAbove || isEditorLayout ? (
          <div className="w-44 flex items-center">
            <Logo isLink />
          </div>
        ) : (
          <>
            {!shouldHideSidebarButton && !isEditorLayout ? (
              <IconButton
                label={t('open-sidebar')}
                onClick={() => props.setSidebarOpen(true)}
              >
                <Menu className="h-5 w-5" aria-hidden="true" />
              </IconButton>
            ) : (
              <div className="flex items-center">
                <Logo isLink />
              </div>
            )}
            <div
              className="h-6 w-px bg-[var(--tn-border)]/70 mx-1"
              aria-hidden="true"
            />
          </>
        )}

        <div className="flex-1" />

        <div className="flex items-center gap-x-2">
          {isSysOp && !IS_ON_PREM && (
            <Tooltip label={t('back-office')} placement="bottom">
              <IconButton
                as="a"
                href="/back-office/customers"
                target="_self"
                label={t('back-office')}
              >
                <Landmark className="h-5 w-5" />
              </IconButton>
            </Tooltip>
          )}

          {IS_ON_PREM && (
            <Tooltip label={t('admin-panel')} placement="bottom">
              <IconButton
                label={t('admin-panel')}
                onClick={() => navigate(PATHS().ADMIN_DASHBOARD)}
              >
                <Settings className="h-5 w-5" />
              </IconButton>
            </Tooltip>
          )}

          {!IS_ON_PREM && (
            <Tooltip label={t('status')} placement="bottom">
              <IconButton
                label={t('status')}
                onClick={() => navigate(STATUS_PATH)}
                isActive={isStatus}
              >
                <ActivitySquare className="h-5 w-5" />
              </IconButton>
            </Tooltip>
          )}

          {!IS_ON_PREM && (
            <Tooltip label={t('feedback')} placement="bottom">
              <IconButton
                as="a"
                href="https://feedback.scriptix.io/"
                target="_blank"
                label={t('feedback')}
              >
                <MessageSquareMore className="h-5 w-5" />
              </IconButton>
            </Tooltip>
          )}

          <Tooltip label={t('docs')} placement="bottom">
            <IconButton
              as="a"
              href="https://docs.scriptix.io/"
              target="_blank"
              label={t('docs')}
            >
              <BookOpenCheck className="h-5 w-5" />
            </IconButton>
          </Tooltip>

          <div
            className="hidden lg:block h-5 w-px bg-[var(--tn-border)]/70"
            aria-hidden="true"
          />

          <Tooltip label={t('profile')} placement="bottom">
            <IconButton
              label={t('profile')}
              onClick={() => navigate(PROFILE_PATH)}
              isActive={isProfile}
            >
              <UserRound className="h-5 w-5" />
            </IconButton>
          </Tooltip>

          <Tooltip label={t('log-out')} placement="bottom">
            <IconButton
              label={t('log-out')}
              onClick={() => logout({ isManual: true })}
            >
              <LogOut className="h-5 w-5" />
            </IconButton>
          </Tooltip>
        </div>
      </div>
    </nav>
  );
}
